import type { CSSProperties } from 'react';
import type { AnalysisType } from '../../shared/types';

/* ── Identité colorée des types d'analyse (mode pastel) ───────────────
   Chaque type porte une teinte du nuancier : elle habille sa tuile sur
   l'accueil, puis, une fois l'analyse ouverte, l'en-tête, les champs et
   les cartes de l'écran — pendant que le fond reste crème. La couleur
   sert de repère de navigation.

   Le code ne connaît que le SLOT (a…e) : les couleurs réelles vivent dans
   client/index.css, un bloc par nuancier (`.pastel[data-palette="…"]`).
   Hors mode pastel, ces variables ne sont lues par personne. */

export type TintSlot = 'a' | 'b' | 'c' | 'd' | 'e' | 'n';

/* Cinq teintes pour neuf types : les doublons sont volontairement éloignés
   dans la grille pour rester distinguables (même carte que Visaly). */
const BY_ANALYSIS: Record<AnalysisType, TintSlot> = {
  general_question: 'c',
  brand_name: 'a',
  slogan: 'b',
  concept: 'd',
  label: 'e',
  campaign: 'a',
  social_post: 'b',
  event_activation: 'c',
  creative_brief: 'd',
};

/** Slot de teinte d'un type d'analyse. Retombe sur le neutre crème si inconnu. */
export function tintSlot(key: AnalysisType | string | null | undefined): TintSlot {
  if (!key) return 'n';
  return BY_ANALYSIS[key as AnalysisType] ?? 'n';
}

/** Variables CSS `--tint-*` à poser sur un conteneur : tout ce qu'il contient
 *  peut alors se colorer via `var(--tint)` sans connaître le type courant. */
export function tintVars(key: AnalysisType | string | null | undefined): CSSProperties {
  const s = tintSlot(key);
  return {
    '--tint': `var(--p-${s})`,
    '--tint-sig': `var(--p-${s}-sig)`,
    '--tint-soft': `var(--p-${s}-soft)`,
    '--tint-pale': `var(--p-${s}-pale)`,
    '--tint-field': `var(--p-${s}-field)`,
    '--tint-strong': `var(--p-${s}-strong)`,
    '--tint-deep': `var(--p-${s}-deep)`,
  } as CSSProperties;
}

/** Valeur CSS de la couleur signature d'un type (pour un `fill` SVG, etc.). */
export function tintSignature(key: AnalysisType | string | null | undefined): string {
  return `var(--p-${tintSlot(key)}-sig)`;
}
